import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Skeleton, Table, TableProps } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { AxiosError, AxiosResponse } from 'axios';
import { UseMutationResult, useQueryClient, UseQueryResult } from 'react-query';

import { mapFilterToSearchQuery } from '../common/filterHelpers';
import { Filter, FiltersOptions } from '../common/filters.type';
import { SearchFormProps } from '../common/searchFormField.types';
import {
  headerPaddingBottom,
  headerPaddingTop,
  StyledLinkedTable,
  TableViewContainer,
} from '../common/tableView.styles';
import { SearchQueryRequestType } from '../common/types';
import { useHeaderHeight } from '../common/useHeaderHeight';
import { useTableDataFilters } from '../common/useTableDataFilters';
import { useTableDataHeight } from '../common/useTableDataHeight';
import { useToggle } from '../common/useToggle';
import { PageRoutesEnum } from '../utils/routes';

import { FilteredTableHeader } from './FilteredTableHeader';
import { SearchDrawer } from './SearchDrawer';

const tableHeaderRowHeight = 55;
const paginationFooterHeight = 67;

type TableType<T extends object> = {
  columns: ColumnsType<T>;
  rowCount?: number;
};

const SkeletonTable = <T extends object>({ columns, rowCount = 10 }: TableType<T>) => {
  return (
    <StyledLinkedTable
      rowKey="key"
      pagination={false}
      dataSource={[...Array(rowCount)].map((_, index) => ({ key: `key${index}` })) as unknown as T[]}
      columns={columns.map((column) => {
        const dataIndex = 'dataIndex' in column ? column.dataIndex : undefined;
        return {
          ...column,
          render: function renderPlaceholder() {
            return <Skeleton key={String(dataIndex)} title={true} paragraph={false} active round />;
          },
        };
      })}
    />
  );
};

type MainTablePageProps<TResponse extends { count: number }, TData extends object> = {
  mode?: 'saved';
  getFilters: () => UseQueryResult<FiltersOptions>;
  saveFilter: () => UseMutationResult<
    AxiosResponse<unknown>,
    AxiosError<unknown, unknown>,
    { id?: string; data?: unknown },
    unknown
  >;
  updateFilter: () => UseMutationResult<
    AxiosResponse<unknown>,
    AxiosError<unknown, unknown>,
    { queryParams?: object; data?: unknown },
    unknown
  >;
  deleteFilter: () => UseMutationResult<
    AxiosResponse<unknown>,
    AxiosError<unknown, unknown>,
    { queryParams?: object; data?: unknown },
    unknown
  >;
  getColumnsDefinition: ({
    sortedInfo,
    withLocalSorting,
  }: {
    sortedInfo: { field: string; order: string }[];
    withLocalSorting?: boolean;
  }) => ColumnsType<TData>;
  search: (query: SearchQueryRequestType) => UseQueryResult<TResponse>;
  getDataSource: (param: TResponse | undefined) => TData[];
  storedFilterName: string;
  storedPagingData: string;
  title: string;
  searchForm: FunctionComponent<SearchFormProps>;
  addPageRoute?: PageRoutesEnum;
  defaultFilter?: Filter;
  headerActions?: React.ReactNode;
};

type ControlledTablePageProps<TData extends object> = {
  mode: 'controlled';
  header: React.ReactNode;
  toolbar: React.ReactNode;
  tableProps: TableProps<TData>;
  footer?: React.ReactNode;
  className?: string;
};

// Read-only lists can own URL state without inventing saved-filter mutations.
export const MainTablePage = <TResponse extends { count: number }, TData extends object>(
  props: MainTablePageProps<TResponse, TData> | ControlledTablePageProps<TData>
) =>
  props.mode === 'controlled' ? (
    <section className={props.className}>
      {props.header}
      {props.toolbar}
      <Table<TData> {...props.tableProps} />
      {props.footer}
    </section>
  ) : (
    <SavedFilterTablePage {...props} />
  );

const SavedFilterTablePage = <TResponse extends { count: number }, TData extends object>({
  getFilters,
  saveFilter,
  updateFilter,
  deleteFilter,
  getColumnsDefinition,
  search,
  getDataSource,
  storedFilterName,
  storedPagingData,
  title,
  searchForm: SearchForm,
  addPageRoute,
  defaultFilter,
  headerActions,
}: MainTablePageProps<TResponse, TData>) => {
  const queryClient = useQueryClient();
  const saveFilterMutation = saveFilter();
  const updateFilterMutation = updateFilter();
  const deleteFilterMutation = deleteFilter();
  const [filtersDisplayed, toggleFiltersDisplayed] = useToggle(false);

  const { data: filtersOptions, isLoading } = getFilters();
  const hasRestoredStoredState = useRef(false);
  const [canPersistFilter, setCanPersistFilter] = useState(false);

  const {
    currentFilter,
    pagingFrom,
    pageNumber,
    handleChangeSortingAndPagination,
    handleChangeFilter,
    deleteCriteria,
    resetFilterToDefault,
    addCriteria,
    pageSize,
    clearFilters,
  } = useTableDataFilters(defaultFilter, storedPagingData);

  const resetFilterToCurrent = () => {
    const filterToSet = filtersOptions?.filters.find((option) => option.id === currentFilter.id);
    if (!filterToSet) return;
    handleChangeFilter(filterToSet);
  };

  useEffect(() => {
    if (isLoading || !filtersOptions || hasRestoredStoredState.current) return;
    hasRestoredStoredState.current = true;

    const storedFilter = JSON.parse(localStorage.getItem(storedFilterName) ?? '{}');

    const defaultFilter =
      Object.keys(storedFilter).length > 0
        ? storedFilter
        : filtersOptions?.filters.find((filter: Filter) => filter.isDefault);

    if (defaultFilter) {
      handleChangeFilter(defaultFilter);
    }

    const storedPaging = JSON.parse(localStorage.getItem(storedPagingData) ?? '{}');
    handleChangeSortingAndPagination({
      current: storedPaging.current,
      pageSize: storedPaging.pageSize,
      total: storedPaging.total,
    });
    setCanPersistFilter(true);
  }, [
    filtersOptions,
    filtersOptions?.filters,
    handleChangeFilter,
    handleChangeSortingAndPagination,
    isLoading,
    storedFilterName,
    storedPagingData,
  ]);

  useEffect(() => {
    if (!canPersistFilter || !currentFilter.name) return;
    localStorage.setItem(storedFilterName, JSON.stringify(currentFilter));
  }, [canPersistFilter, currentFilter, storedFilterName]);

  const reloadData = () => {
    queryClient.invalidateQueries({
      predicate: (query) => typeof query.queryKey[0] === 'string' && query.queryKey[0].includes('/search'),
    });
  };

  const columns = getColumnsDefinition({ sortedInfo: currentFilter.sort });
  const { data, isLoading: dataLoading } = search(mapFilterToSearchQuery(currentFilter, pagingFrom, pageSize));

  const dataSource = getDataSource(data);
  const pagination = { pageSize, total: data?.count, current: pageNumber, position: ['none', 'bottomCenter'] };

  const { headerHeight, headerRef } = useHeaderHeight();

  const { tableHeight } = useTableDataHeight(headerHeight + tableHeaderRowHeight, paginationFooterHeight);

  return (
    <TableViewContainer>
      <SearchDrawer
        open={filtersDisplayed}
        close={toggleFiltersDisplayed}
        filters={filtersOptions?.filters}
        handleSubmit={handleChangeFilter}
        currentFilter={currentFilter}
        resetFilterToDefault={resetFilterToDefault}
        onSave={saveFilterMutation}
        onUpdate={updateFilterMutation}
        onDelete={deleteFilterMutation}
        searchForm={SearchForm}
      />
      <FilteredTableHeader
        title={title}
        showFilters={toggleFiltersDisplayed}
        currentFilter={currentFilter}
        resetFilter={clearFilters}
        addCriteria={addCriteria}
        deleteCriteria={deleteCriteria}
        ref={headerRef}
        addPageRoute={addPageRoute}
        headerActions={headerActions}
        resetFilterToCurrent={resetFilterToCurrent}
        reloadData={reloadData}
      />
      {dataLoading ? (
        <SkeletonTable columns={columns} />
      ) : (
        <StyledLinkedTable
          padding={`${headerPaddingTop}px 0px ${headerPaddingBottom}px 0px`}
          columns={columns}
          dataSource={dataSource}
          pagination={pagination}
          onChange={handleChangeSortingAndPagination}
          scroll={{ y: tableHeight }}
          bordered
        />
      )}
    </TableViewContainer>
  );
};
